import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// NOT: Bu controller (/api/customer-notes) OData DEĞİL — REST'tir
// (?status=&take= ile filtreler, ApiResponse<{items,openCount,...}> döner;
// $filter/$orderby/$top/$skip desteklemez). Bu yüzden useC360List yerine
// yerel (client-side) bir veri motoru kullanılır; c360 görsel bileşenleri
// (ListPageShell + C360Table + ColumnChooser + Pagination) aynen uygulanır.

const API_BASE = '/api/customer-notes';
const PAGE_SIZE_DEFAULT = 10;

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'OPEN', label: 'Açık' },
  { key: 'DONE', label: 'Tamamlanan' },
  { key: 'ARCHIVED', label: 'Arşivlenen' },
];

const STATUS_META = {
  OPEN: { label: 'Açık', tone: 'wait' },
  DONE: { label: 'Tamamlandı', tone: 'ok' },
  ARCHIVED: { label: 'Arşiv', tone: 'neutral' },
};

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Açık' },
  { value: 'DONE', label: 'Tamamlandı' },
  { value: 'ARCHIVED', label: 'Arşiv' },
];

// c360 kolon filtreleri (client-side uygulanır; odataField yalnızca model uyumu için).
const COLUMN_FILTER_META = {
  title: { type: 'text', odataField: 'Title' },
  representativeName: { type: 'text', odataField: 'RepresentativeName' },
  customerTitle: { type: 'text', odataField: 'CustomerTitle' },
  status: { type: 'select', odataField: 'Status', valueFormat: 'string', options: STATUS_OPTIONS },
};
// Serbest metin aramasının tarayacağı (string) alanlar.
const SEARCH_FIELDS = ['title', 'description', 'representativeName', 'customerTitle'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    openCount: pick(data, 'openCount', 'OpenCount') ?? 0,
    doneCount: pick(data, 'doneCount', 'DoneCount') ?? 0,
    archivedCount: pick(data, 'archivedCount', 'ArchivedCount') ?? 0,
  };
}

function statusBadgeFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

// Tek bir kolon filtresini ({op,value,value2}) satıra uygula (client-side).
function matchColumnFilter(meta, filter, rawValue) {
  if (!filter || !meta) return true;
  const op = filter.op;
  const value = filter.value;
  if (op === 'blank') return rawValue === null || rawValue === undefined || rawValue === '';
  if (op === 'notBlank') return !(rawValue === null || rawValue === undefined || rawValue === '');

  if (meta.type === 'text') {
    const cell = String(rawValue ?? '').toLocaleLowerCase('tr-TR');
    const q = String(value ?? '').toLocaleLowerCase('tr-TR');
    switch (op) {
      case 'contains': return cell.includes(q);
      case 'notContains': return !cell.includes(q);
      case 'equals': return cell === q;
      case 'notEquals': return cell !== q;
      case 'startsWith': return cell.startsWith(q);
      case 'endsWith': return cell.endsWith(q);
      default: return true;
    }
  }
  if (meta.type === 'select') {
    return String(rawValue ?? '') === String(value ?? '');
  }
  return true;
}

export function CustomerNotesPage() {
  usePageMeta('Saha Notları', 'Saha temsilcisinin kişisel notları (admin görüntüleme/düzenleme).');
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePageActions();

  const [statusFilter, setStatusFilter] = useState('');

  const [allRows, setAllRows] = useState([]);
  const [counts, setCounts] = useState({ openCount: 0, doneCount: 0, archivedCount: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // c360 görünüm state'i
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setAllRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ openCount: data.openCount, doneCount: data.doneCount, archivedCount: data.archivedCount });
      setCurrentPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  // === client-side filtre + sıralama ===
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const matchSearch = (row) => {
      if (!needle) return true;
      return SEARCH_FIELDS.some((f) => {
        const v = row[f];
        return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
      });
    };
    const matchColumns = (row) => Object.entries(columnFilters).every(([colId, filter]) => {
      const meta = COLUMN_FILTER_META[colId];
      return matchColumnFilter(meta, filter, row[colId]);
    });
    const filtered = allRows.filter((r) => matchSearch(r) && matchColumns(r));

    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Title': return String(row.title || '');
        case 'RepresentativeName': return String(row.representativeName || '');
        case 'CustomerTitle': return String(row.customerTitle || '');
        case 'Status': return String(row.status || '');
        case 'UpdatedDate':
        default: { const v = row.updatedDate; return v ? new Date(v).getTime() : 0; }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  }, []);
  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  }, []);
  const applySort = useCallback((field, dir) => {
    setSortField(field || 'UpdatedDate');
    setSortDir(dir || 'asc');
    setCurrentPage(1);
  }, []);

  // === Navigasyon (tam-sayfa detay) ===
  // Görüntüleme/düzenleme artık tam-sayfa /customer-notes/:id ekranına gider; satır
  // state ile taşınır (temsilci adı + müşteri başlığı detayda hazır gelsin diye).
  const openDetail = (row) => {
    if (!row) return;
    const id = pick(row, 'id', 'Id');
    navigate(`/customer-notes/${id}`, { state: { row } });
  };
  // #282: kalem (Değiştir) düzenleme moduna açar
  const openEditRow = (row) => {
    if (!row) return;
    const id = pick(row, 'id', 'Id');
    navigate(`/customer-notes/${id}?edit=1`, { state: { row } });
  };

  const handleDelete = async () => {
    if (!selected) return;
    const id = pick(selected, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Not silindi.');
      setDeleteConfirm(false);
      setSelected(null);
      await load();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setDeleteConfirm(false);
    } finally {
      setDeleteBusy(false);
    }
  };

  // === kolonlar ===
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('title', {
      header: 'Not', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const desc = row.original.description;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 700 }}>{row.original.title || '-'}</span>
            {desc && (
              <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>
                {String(desc).slice(0, 100)}{String(desc).length > 100 ? '…' : ''}
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('representativeName', {
      header: 'Temsilci', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('customerTitle', {
      header: 'Müşteri', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '(Genel)',
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => statusBadgeFor(info.getValue()),
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'title', headerName: 'Not' },
        { field: 'description', headerName: 'Açıklama' },
        { field: 'representativeName', headerName: 'Temsilci' },
        { field: 'customerTitle', headerName: 'Müşteri' },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[row.status]?.label || row.status || '-') },
        { headerName: 'Güncelleme', valueGetter: ({ row }) => formatDate(row.updatedDate) },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `saha-notlari_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      {STATUS_TABS.map((tab) => (
        <button
          key={tab.key || 'all'}
          type="button"
          style={{ ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary), padding: '6px 14px', fontSize: 13 }}
          onClick={() => setStatusFilter(tab.key)}
        >
          {tab.label}
        </button>
      ))}
      <input
        style={s.filterInput}
        type="text"
        placeholder="Not / temsilci / müşteri ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load} disabled={loading}>{loading ? 'Yükleniyor...' : 'Yenile'}</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  const headerMeta = (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      <StatusBadge tone="wait">Açık: {counts.openCount}</StatusBadge>
      <StatusBadge tone="ok">Tamamlanan: {counts.doneCount}</StatusBadge>
      <StatusBadge tone="neutral">Arşiv: {counts.archivedCount}</StatusBadge>
    </span>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Saha Notları"
        subtitle="Saha temsilcisinin kişisel notları. Yeni not yaratma sadece mobile'dan; admin görüntüler, içerik/durum günceller veya siler."
        meta={headerMeta}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{pick(selected, 'title', 'Title')}"</strong> notunu silmek istediğinize emin misiniz?
              </p>
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
                Mobile bu notu bir sonraki sync'te artık görmeyecek.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }} onClick={handleDelete} disabled={deleteBusy}>{deleteBusy ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)} disabled={deleteBusy}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CustomerNotesPage;
